import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';
import { HeaderService } from '@core/services/header.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';

@Component({
  standalone: false,
  selector: 'app-roster',
  templateUrl: './roster.component.html',
  styleUrls: ['./roster.component.scss']
})
export class RosterComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  loading = true;
  activeTab: 'pattern' | 'shifts' | 'requests' = 'pattern';
  showShiftDefs = false;
  employees: any[] = [];
  shifts: any[] = [];
  assignments: any[] = [];
  departments: any[] = [];
  holidays: any[] = [];

  searchText = '';
  filterDept = 'all';
  filterShift = 'all';

  showForm = false;
  form: any = this.emptyForm();
  submitting = false;
  busy: { [id: string]: boolean } = {};

  weekStart = '';
  weekDays: { code: string; date: string; dow: number }[] = [];
  readonly DAY_CODES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  readonly WEEKDAY_PICKER = [
    { code: 'MON', label: 'Mon' }, { code: 'TUE', label: 'Tue' }, { code: 'WED', label: 'Wed' },
    { code: 'THU', label: 'Thu' }, { code: 'FRI', label: 'Fri' }, { code: 'SAT', label: 'Sat' },
  ];
  showPattern = false;
  patternEmp: any = null;
  patternForm: any = { workType: 'Office', officeDays: [] as string[] };
  patternSaving = false;

  myUserId = '';

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private headerService: HeaderService,
    private snackbar: SnackbarService,
    private confirmDialog: ConfirmDialogService,
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('Roster');
    this.myUserId = this.authService.getCurrentUser()?.id || '';
    this.setWeek(new Date());
    this.load();
  }

  private iso(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  setWeek(base: Date): void {
    const d = new Date(base);
    const dow = d.getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(d); monday.setDate(d.getDate() + mondayOffset);
    this.weekStart = this.iso(monday);
    this.weekDays = [];
    for (let i = 0; i < 6; i++) {
      const dd = new Date(monday); dd.setDate(monday.getDate() + i);
      this.weekDays.push({ code: this.DAY_CODES[dd.getDay()], date: this.iso(dd), dow: dd.getDay() });
    }
  }
  shiftWeek(delta: number): void {
    const s = new Date(this.weekStart); s.setDate(s.getDate() + delta * 7);
    this.setWeek(s);
  }

  isPureManager(): boolean {
    return (this.authService.getCurrentUser()?.role || '').toLowerCase().replace(/^role_/, '') === 'manager';
  }
  isHrAdmin(): boolean {
    const r = (this.authService.getCurrentUser()?.role || '').toLowerCase().replace(/^role_/, '');
    return ['hr', 'hr_admin', 'admin', 'super_admin'].includes(r);
  }

  notifyingId: { [id: string]: boolean } = {};
  notifyPattern(emp: any): void {
    if (!emp?.id) return;
    this.notifyingId[emp.id] = true;
    this.apiService.post(`/employees/${emp.id}/notify-work-pattern`, {}).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => { this.notifyingId[emp.id] = false; this.snackbar.showSuccess(res?.message || 'Notification sent'); },
      error: () => { this.notifyingId[emp.id] = false; this.snackbar.showFailure('Could not notify'); }
    });
  }

  notifyingAll = false;
  async notifyAll(): Promise<void> {
    const targets = this.rows.map(r => r.emp);
    if (!targets.length) { this.snackbar.showFailure('No employees in the current filter'); return; }
    if (!(await this.confirmDialog.confirm(`Notify ${targets.length} employee(s) (and their managers + HR) about their current work pattern?`, { title: 'Send Notifications', confirmLabel: 'Notify' }))) return;
    this.notifyingAll = true;
    let done = 0;
    targets.forEach(emp => {
      this.apiService.post(`/employees/${emp.id}/notify-work-pattern`, {}).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => { if (++done === targets.length) { this.notifyingAll = false; this.snackbar.showSuccess(`Notified ${targets.length} employee(s)`); } },
        error: () => { if (++done === targets.length) { this.notifyingAll = false; this.snackbar.showSuccess('Notifications sent'); } }
      });
    });
  }

  emptyForm(): any {
    return { employeeId: '', shiftId: '', date: new Date().toISOString().split('T')[0], notes: '' };
  }

  private normalize(res: any): any[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.data)) return res.data;
    return [];
  }

  closeShiftDefs(): void {
    this.showShiftDefs = false;
    this.apiService.get('/shifts').pipe(catchError(() => of([])), takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => { this.shifts = this.normalize(data); this.rebuildRows(); });
  }

  load(): void {
    this.loading = true;
    forkJoin({
      employees:   this.apiService.get('/employees').pipe(catchError(() => of([]))),
      shifts:      this.apiService.get('/shifts').pipe(catchError(() => of([]))),
      roster:      this.apiService.get('/roster').pipe(catchError(() => of([]))),
      departments: this.apiService.get('/departments').pipe(catchError(() => of([]))),
      holidays:    this.apiService.get('/holidays').pipe(catchError(() => of([]))),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.employees   = this.normalize(data.employees).filter((e: any) => (e.status || 'active').toLowerCase() !== 'inactive');
        this.shifts      = this.normalize(data.shifts);
        this.assignments = this.normalize(data.roster);
        this.departments = this.normalize(data.departments);
        this.holidays    = this.normalize(data.holidays);
        this.rebuildRows();
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  empName(e: any): string { return `${e.firstName || ''} ${e.lastName || ''}`.trim() || 'Unknown'; }
  initials(e: any): string { return ((e.firstName?.[0] || '') + (e.lastName?.[0] || '')).toUpperCase() || '?'; }
  deptName(id: string): string { return this.departments.find(d => d.id === id)?.name || '—'; }
  shiftName(id: string): string { return this.shifts.find(s => s.id === id)?.name || '—'; }
  shiftTime(id: string): string {
    const s = this.shifts.find(x => x.id === id);
    if (!s) return '';
    return `${(s.startTime || '').slice(0, 5)} – ${(s.endTime || '').slice(0, 5)}`;
  }
  shiftColor(id: string): string { return this.shifts.find(s => s.id === id)?.color || '#334155'; }

  currentAssignment(empId: string): any {
    return this.assignments
      .filter(a => a.employeeId === empId)
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0] || null;
  }

  get shiftOptions() {
    return [{ label: 'All Shifts', value: 'all' }, ...this.shifts.map(s => ({ label: s.name, value: s.id }))];
  }
  get deptOptions() {
    return [{ label: 'All Departments', value: 'all' }, ...this.departments.map(d => ({ label: d.name, value: d.id }))];
  }

  private _rows: any[] = [];
  get rows(): any[] { return this._rows; }
  trackByEmp = (_: number, r: any) => r?.emp?.id || _;

  rebuildRows(): void {
    const term = (this.searchText || '').toLowerCase();
    const pm = this.isPureManager();
    this._rows = this.employees
      .map(e => {
        const a = this.currentAssignment(e.id);
        return { emp: e, assignment: a, shiftId: a?.shiftId || null, effectiveFrom: a?.date || null };
      })
      .filter(r => !pm || r.emp.managerId === this.myUserId)
      .filter(r => this.filterDept === 'all' || r.emp.departmentId === this.filterDept)
      .filter(r => this.filterShift === 'all'
        ? true
        : (this.filterShift === 'unassigned' ? !r.shiftId : r.shiftId === this.filterShift))
      .filter(r => !term || this.empName(r.emp).toLowerCase().includes(term)
        || (r.emp.employeeId || '').toLowerCase().includes(term))
      .sort((a, b) => this.empName(a.emp).localeCompare(this.empName(b.emp)));
  }

  get assignedCount(): number { return this._rows.filter(r => r.shiftId).length; }
  get unassignedCount(): number { return this._rows.filter(r => !r.shiftId).length; }

  get assignableEmployees(): any[] {
    return this.isPureManager() ? this.employees.filter(e => e.managerId === this.myUserId) : this.employees;
  }

  workTypeOf(emp: any): string {
    if (emp.workType) return emp.workType;
    const loc = (emp.location || '').toLowerCase();
    if (loc === 'remote') return 'Remote';
    if (loc === 'hybrid') return 'Hybrid';
    return 'Office';
  }
  officeDaysOf(emp: any): string[] {
    return (emp.officeDays || '').split(',').map((s: string) => s.trim().toUpperCase()).filter(Boolean);
  }
  titleDay(code: string): string { return code ? code.charAt(0) + code.slice(1).toLowerCase() : ''; }
  patternLabel(emp: any): string {
    const wt = this.workTypeOf(emp);
    if (wt === 'Office') return 'Mon–Fri';
    if (wt === 'Remote') return 'Fully remote';
    const days = this.officeDaysOf(emp);
    return days.length ? days.map(d => this.titleDay(d)).join(', ') : 'Not set';
  }
  isHoliday(date: string): boolean { return this.holidays.some(h => (h.date || '').toString().slice(0, 10) === date); }
  holidayName(date: string): string { return this.holidays.find(h => (h.date || '').toString().slice(0, 10) === date)?.name || 'Holiday'; }

  dayState(emp: any, day: { code: string; date: string; dow: number }): string {
    if (this.isHoliday(day.date)) return 'holiday';
    const wt = this.workTypeOf(emp);
    const isWeekend = day.dow === 0 || day.dow === 6;
    if (wt === 'Office') return isWeekend ? 'off' : 'wfo';
    if (wt === 'Remote') return isWeekend ? 'off' : 'wfh';
    if (this.officeDaysOf(emp).includes(day.code)) return 'wfo';
    return isWeekend ? 'off' : 'wfh';
  }
  cellLabel(state: string): string {
    return state === 'wfo' ? 'WFO' : state === 'wfh' ? 'WFH' : state === 'holiday' ? 'Holiday' : 'Off';
  }

  private officeSet(emp: any): string[] {
    const wt = this.workTypeOf(emp);
    if (wt === 'Office') return ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    if (wt === 'Remote') return [];
    return this.officeDaysOf(emp);
  }

  workTypeOptions = ['Office', 'Remote', 'Hybrid'];

  private persistPattern(emp: any, workType: string, officeDays: string, silent = false): void {
    const prev = { workType: emp.workType, officeDays: emp.officeDays };
    emp.workType = workType; emp.officeDays = officeDays;
    this.apiService.patch(`/employees/${emp.id}/work-pattern`, { workType, officeDays }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { if (!silent) this.snackbar.showSuccess(`${this.empName(emp)} → ${workType}`); },
      error: () => {
        emp.workType = prev.workType; emp.officeDays = prev.officeDays;
        this.snackbar.showFailure('Could not save — reverted');
      }
    });
  }

  private deriveAndPersist(emp: any, set: Set<string>, silent = false): void {
    const weekdays = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
    const ordered = this.DAY_CODES.filter(c => set.has(c));
    let workType: string, officeDays: string;
    if (ordered.length === 0) { workType = 'Remote'; officeDays = ''; }
    else if (ordered.length === 5 && weekdays.every(d => set.has(d))) { workType = 'Office'; officeDays = ''; }
    else { workType = 'Hybrid'; officeDays = ordered.join(','); }
    this.persistPattern(emp, workType, officeDays, silent);
  }

  setWorkType(emp: any, type: string): void {
    let officeDays = '';
    if (type === 'Hybrid') {
      const existing = this.officeDaysOf(emp);
      officeDays = this.DAY_CODES.filter(c => (existing.length ? existing : ['TUE', 'WED', 'THU']).includes(c)).join(',');
    }
    this.persistPattern(emp, type, officeDays);
  }

  toggleDay(emp: any, code: string): void {
    const set = new Set(this.officeSet(emp));
    if (set.has(code)) set.delete(code); else set.add(code);
    this.deriveAndPersist(emp, set, true);
  }

  toggleCell(emp: any, day: { code: string; date: string; dow: number }): void {
    if (this.isHoliday(day.date)) return;
    const set = new Set(this.officeSet(emp));
    if (set.has(day.code)) set.delete(day.code); else set.add(day.code);
    this.deriveAndPersist(emp, set, true);
  }

  bulkType = 'Office';
  bulkDays: string[] = ['TUE', 'WED', 'THU'];
  toggleBulkDay(code: string): void {
    const i = this.bulkDays.indexOf(code);
    if (i >= 0) this.bulkDays.splice(i, 1); else this.bulkDays.push(code);
  }
  async applyBulk(): Promise<void> {
    const targets = this.rows.map(r => r.emp);
    if (!targets.length) { this.snackbar.showFailure('No employees in the current filter'); return; }
    let officeDays = '';
    if (this.bulkType === 'Hybrid') {
      if (!this.bulkDays.length) { this.snackbar.showFailure('Pick at least one office day'); return; }
      officeDays = this.DAY_CODES.filter(c => this.bulkDays.includes(c)).join(',');
    }
    const scope = this.filterDept === 'all' ? 'all shown' : `the ${this.deptName(this.filterDept)} department`;
    if (!(await this.confirmDialog.confirm(`Apply ${this.bulkType} pattern to ${targets.length} employee(s) in ${scope}?`, { title: 'Apply Pattern', confirmLabel: 'Apply' }))) return;
    targets.forEach(emp => this.persistPattern(emp, this.bulkType, officeDays, true));
    this.snackbar.showSuccess(`Applied ${this.bulkType} pattern to ${targets.length} employee(s)`);
  }

  showEmpDrawer = false;
  drawerEmp: any = null;
  empHistory: any[] = [];
  historyLoading = false;

  openEmpDrawer(emp: any): void {
    this.drawerEmp = emp;
    this.showEmpDrawer = true;
    this.empHistory = [];
    this.historyLoading = true;
    this.apiService.get(`/roster-history?employeeId=${emp.id}`).pipe(catchError(() => of([])))
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => { this.empHistory = this.normalize(res); this.historyLoading = false; });
  }
  closeEmpDrawer(): void { this.showEmpDrawer = false; this.drawerEmp = null; this.empHistory = []; }

  drawerCurrentShift(): string {
    const a = this.currentAssignment(this.drawerEmp?.id);
    return a?.shiftId ? this.shiftName(a.shiftId) : 'Not assigned';
  }

  exportExcel(): void {
    const header = ['Employee', 'Emp ID', 'Department', 'Work Type', 'Office Days',
      ...this.weekDays.map(d => `${this.titleDay(d.code)} ${d.date.slice(5)}`)];
    const aoa: any[][] = [header];
    this.rows.forEach(r => {
      aoa.push([
        this.empName(r.emp), r.emp.employeeId || '', this.deptName(r.emp.departmentId),
        this.workTypeOf(r.emp), this.patternLabel(r.emp),
        ...this.weekDays.map(d => this.cellLabel(this.dayState(r.emp, d))),
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = header.map((_, i) => ({ wch: i < 5 ? 22 : 10 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Roster');
    XLSX.writeFile(wb, `Roster_${this.weekDays[0]?.date}_to_${this.weekDays[5]?.date}.xlsx`);
    this.snackbar.showSuccess('Roster exported');
  }

  openPattern(emp: any): void {
    this.patternEmp = emp;
    this.patternForm = { workType: this.workTypeOf(emp), officeDays: this.officeDaysOf(emp) };
    if (this.patternForm.workType === 'Hybrid' && !this.patternForm.officeDays.length) {
      this.patternForm.officeDays = ['TUE', 'WED', 'THU'];
    }
    this.showPattern = true;
  }
  closePattern(): void { this.showPattern = false; this.patternEmp = null; }
  onPatternTypeChange(): void {
    if (this.patternForm.workType === 'Hybrid' && !this.patternForm.officeDays.length) {
      this.patternForm.officeDays = ['TUE', 'WED', 'THU'];
    }
  }
  togglePatternDay(code: string): void {
    const i = this.patternForm.officeDays.indexOf(code);
    if (i >= 0) this.patternForm.officeDays.splice(i, 1);
    else this.patternForm.officeDays.push(code);
  }
  savePattern(): void {
    if (!this.patternEmp) return;
    if (this.patternForm.workType === 'Hybrid' && !this.patternForm.officeDays.length) {
      this.snackbar.showFailure('Pick at least one office day for Hybrid');
      return;
    }
    this.patternSaving = true;
    const officeDays = this.patternForm.workType === 'Hybrid'
      ? this.DAY_CODES.filter(c => this.patternForm.officeDays.includes(c)).join(',')
      : '';
    this.apiService.patch(`/employees/${this.patternEmp.id}/work-pattern`, {
      workType: this.patternForm.workType,
      officeDays: officeDays,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.patternEmp.workType = this.patternForm.workType;
        this.patternEmp.officeDays = officeDays;
        this.patternSaving = false;
        this.showPattern = false;
        this.snackbar.showSuccess('Work pattern updated');
      },
      error: (err: any) => {
        this.patternSaving = false;
        this.snackbar.showFailure(err?.error?.message || 'Could not update pattern');
      }
    });
  }

  openAssign(row?: any): void {
    this.form = this.emptyForm();
    if (row) {
      this.form.employeeId = row.emp.id;
      this.form.shiftId = row.shiftId || '';
    }
    this.showForm = true;
  }
  closeForm(): void { this.showForm = false; }

  saveAssignment(): void {
    if (!this.form.employeeId) { this.snackbar.showFailure('Select an employee'); return; }
    if (!this.form.shiftId) { this.snackbar.showFailure('Select a shift'); return; }
    if (!this.form.date) { this.snackbar.showFailure('Set the effective date'); return; }

    this.submitting = true;
    this.apiService.post('/roster', {
      employeeId: this.form.employeeId,
      shiftId: this.form.shiftId,
      date: this.form.date,
      notes: this.form.notes || '',
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.submitting = false;
        this.showForm = false;
        this.snackbar.showSuccess('Shift assigned');
        this.load();
      },
      error: (err: any) => {
        this.submitting = false;
        this.snackbar.showFailure(err?.error?.message || 'Could not assign shift');
      }
    });
  }

  removeAssignment(row: any): void {
    const a = row.assignment;
    if (!a?.id) return;
    this.busy[a.id] = true;
    this.apiService.delete(`/roster/${a.id}`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.busy[a.id] = false; this.snackbar.showSuccess('Assignment removed'); this.load(); },
      error: () => { this.busy[a.id] = false; this.snackbar.showFailure('Could not remove'); }
    });
  }

  employeeOptions() {
    return this.employees.map(e => ({ label: `${this.empName(e)} (${e.employeeId || '—'})`, value: e.id }));
  }
  shiftPickerOptions() {
    return this.shifts.map(s => ({ label: `${s.name} · ${(s.startTime || '').slice(0, 5)}–${(s.endTime || '').slice(0, 5)}`, value: s.id }));
  }
}
